// CSS keyframe animation engine. Each element -> { className, css } or null.
import { attrToSvgName } from "./utils";
import { fillRef, filterRef, effectDefsString } from "./effects";
import { bboxOf, centerOf } from "./canvasUtils";
import { resolveCornerRadii, roundedRectPath, hasCornerRadius, roundedPolygonPath, parsePoints, resolveCornerRadius } from "./cornerRadius";
import { isArrow, splitArrowPath, isFilledDeco } from "./lineEdit";

const ORIGIN_MAP = {
  center: "center",
  top: "center top",
  bottom: "center bottom",
  left: "left center",
  right: "right center",
  topLeft: "left top",
  topRight: "right top",
  bottomLeft: "left bottom",
  bottomRight: "right bottom",
};

function keyframesFor(kf, a) {
  const f = a.from;
  const t = a.to;
  switch (a.type) {
    case "fade":
      return `@keyframes ${kf}{from{opacity:${f ?? 0}}to{opacity:${t ?? 1}}}`;
    case "fadeUp":
      return `@keyframes ${kf}{from{opacity:0;transform:translateY(${a.to ?? 24}px)}to{opacity:1;transform:translateY(0)}}`;
    case "fadeDown":
      return `@keyframes ${kf}{from{opacity:0;transform:translateY(-${Math.abs(a.to ?? 24)}px)}to{opacity:1;transform:translateY(0)}}`;
    case "fadeLeft":
      return `@keyframes ${kf}{from{opacity:0;transform:translateX(-${Math.abs(a.to ?? 24)}px)}to{opacity:1;transform:translateX(0)}}`;
    case "fadeRight":
      return `@keyframes ${kf}{from{opacity:0;transform:translateX(${Math.abs(a.to ?? 24)}px)}to{opacity:1;transform:translateX(0)}}`;
    case "popIn":
      return `@keyframes ${kf}{0%{opacity:0;transform:scale(.4)}60%{opacity:1;transform:scale(1.08)}100%{transform:scale(1)}}`;
    case "zoomIn":
      return `@keyframes ${kf}{from{opacity:0;transform:scale(${f ?? 0.2})}to{opacity:1;transform:scale(1)}}`;
    case "zoomOut":
      return `@keyframes ${kf}{from{opacity:1;transform:scale(1)}to{opacity:0;transform:scale(${t ?? 0.2})}}`;
    case "rotate":
      return `@keyframes ${kf}{from{transform:rotate(${f ?? 0}deg)}to{transform:rotate(${t ?? 360}deg)}}`;
    case "scale":
      return `@keyframes ${kf}{0%{transform:scale(${f ?? 0.8})}50%{transform:scale(${t ?? 1.2})}100%{transform:scale(${f ?? 0.8})}}`;
    case "scaleY":
      return `@keyframes ${kf}{0%{transform:scaleY(${f ?? 0.3})}50%{transform:scaleY(${t ?? 1})}100%{transform:scaleY(${f ?? 0.3})}}`;
    case "scaleX":
      return `@keyframes ${kf}{0%{transform:scaleX(${f ?? 0.05})}100%{transform:scaleX(${t ?? 1})}}`;
    case "translateY":
      return `@keyframes ${kf}{0%,100%{transform:translateY(0)}50%{transform:translateY(${t ?? -20}px)}}`;
    case "translateX":
      return `@keyframes ${kf}{0%,100%{transform:translateX(0)}50%{transform:translateX(${t ?? 20}px)}}`;
    case "shake":
      return `@keyframes ${kf}{0%,100%{transform:translateX(0)}25%{transform:translateX(${f ?? -8}px)}75%{transform:translateX(${t ?? 8}px)}}`;
    case "float":
      return `@keyframes ${kf}{0%,100%{transform:translateY(0) rotate(0)}50%{transform:translateY(${t ?? -14}px) rotate(2deg)}}`;
    case "wobble":
      return `@keyframes ${kf}{0%,100%{transform:translateX(0) rotate(0)}25%{transform:translateX(-${Math.abs(t ?? 10)}px) rotate(-5deg)}75%{transform:translateX(${Math.abs(t ?? 10)}px) rotate(5deg)}}`;
    case "swing":
      return `@keyframes ${kf}{0%,100%{transform:rotate(0)}25%{transform:rotate(${t ?? 15}deg)}75%{transform:rotate(${-(t ?? 15)}deg)}}`;
    case "spin3d":
      return `@keyframes ${kf}{from{transform:rotateY(0)}to{transform:rotateY(${t ?? 360}deg)}}`;
    case "flip":
      return `@keyframes ${kf}{0%{transform:perspective(500px) rotateY(0)}100%{transform:perspective(500px) rotateY(360deg)}}`;
    case "flipX":
      return `@keyframes ${kf}{0%{transform:perspective(500px) rotateX(0)}100%{transform:perspective(500px) rotateX(360deg)}}`;
    case "jello":
      return `@keyframes ${kf}{0%,100%{transform:skewX(0) skewY(0)}30%{transform:skewX(-12deg) skewY(-12deg)}45%{transform:skewX(8deg) skewY(8deg)}60%{transform:skewX(-4deg) skewY(-4deg)}}`;
    case "heartbeat":
      return `@keyframes ${kf}{0%{transform:scale(1)}14%{transform:scale(1.25)}28%{transform:scale(1)}42%{transform:scale(1.25)}70%{transform:scale(1)}}`;
    case "rubberBand":
      return `@keyframes ${kf}{0%,100%{transform:scale3d(1,1,1)}30%{transform:scale3d(1.25,.75,1)}40%{transform:scale3d(.75,1.25,1)}50%{transform:scale3d(1.15,.85,1)}65%{transform:scale3d(.95,1.05,1)}75%{transform:scale3d(1.05,.95,1)}}`;
    case "tada":
      return `@keyframes ${kf}{0%,100%{transform:scale3d(1,1,1) rotate(0)}10%,20%{transform:scale3d(.9,.9,1) rotate(-3deg)}30%,50%,70%,90%{transform:scale3d(1.1,1.1,1) rotate(3deg)}40%,60%,80%{transform:scale3d(1.1,1.1,1) rotate(-3deg)}}`;
    case "bounceIn":
      return `@keyframes ${kf}{0%{opacity:0;transform:scale(.3)}20%{transform:scale(1.1)}40%{transform:scale(.9)}60%{opacity:1;transform:scale(1.03)}80%{transform:scale(.97)}100%{opacity:1;transform:scale(1)}}`;
    case "elastic":
      return `@keyframes ${kf}{0%{transform:scale(0)}55%{transform:scale(1.15)}70%{transform:scale(.92)}85%{transform:scale(1.04)}100%{transform:scale(1)}}`;
    case "slideInLeft":
      return `@keyframes ${kf}{from{opacity:0;transform:translateX(-100%)}to{opacity:1;transform:translateX(0)}}`;
    case "slideInRight":
      return `@keyframes ${kf}{from{opacity:0;transform:translateX(100%)}to{opacity:1;transform:translateX(0)}}`;
    case "blink":
      return `@keyframes ${kf}{0%,49%{opacity:1}50%,100%{opacity:${a.to ?? 0.15}}}`;
    case "colorShift":
      return `@keyframes ${kf}{from{fill:${a.fromColor || "#002FA7"}}to{fill:${a.toColor || "#FF2A00"}}}`;
    case "strokeShift":
      return `@keyframes ${kf}{from{stroke:${a.fromColor || "#FF6B00"}}to{stroke:${a.toColor || "#7C3AED"}}}`;
    case "draw":
      return `@keyframes ${kf}{from{stroke-dashoffset:1}to{stroke-dashoffset:0}}`;
    case "drawReverse":
      return `@keyframes ${kf}{from{stroke-dashoffset:0}to{stroke-dashoffset:1}}`;
    case "dash":
      return `@keyframes ${kf}{to{stroke-dashoffset:${a.to ?? -200}}}`;
    case "morphHue":
      return `@keyframes ${kf}{from{filter:hue-rotate(0)}to{filter:hue-rotate(360deg)}}`;
    case "skew":
      return `@keyframes ${kf}{0%,100%{transform:skewX(0)}50%{transform:skewX(${t ?? 12}deg)}}`;
    case "orbit": {
      const r = Math.abs(t ?? 30);
      return `@keyframes ${kf}{from{transform:rotate(0) translateX(${r}px) rotate(0)}to{transform:rotate(360deg) translateX(${r}px) rotate(-360deg)}}`;
    }
    case "flash":
      return `@keyframes ${kf}{0%,50%,100%{opacity:1}25%,75%{opacity:0}}`;
    case "vibrate":
      return `@keyframes ${kf}{0%,100%{transform:translate(0,0)}20%{transform:translate(-2px,2px)}40%{transform:translate(-2px,-2px)}60%{transform:translate(2px,2px)}80%{transform:translate(2px,-2px)}}`;
    case "glitch":
      return `@keyframes ${kf}{0%,100%{transform:translate(0,0) skewX(0)}20%{transform:translate(-4px,2px) skewX(8deg)}40%{transform:translate(4px,-2px) skewX(-8deg)}60%{transform:translate(-3px,-1px) skewX(4deg)}80%{transform:translate(3px,1px) skewX(-4deg)}}`;
    case "breathe":
      return `@keyframes ${kf}{0%,100%{transform:scale(1);opacity:.7}50%{transform:scale(1.12);opacity:1}}`;
    case "jiggle":
      return `@keyframes ${kf}{0%,100%{transform:rotate(0) scale(1)}25%{transform:rotate(-4deg) scale(1.04)}75%{transform:rotate(4deg) scale(1.04)}}`;
    case "wave":
      return `@keyframes ${kf}{0%,100%{transform:translateY(0) skewX(0)}25%{transform:translateY(-8px) skewX(6deg)}75%{transform:translateY(8px) skewX(-6deg)}}`;
    case "pendulum":
      return `@keyframes ${kf}{0%,100%{transform:rotate(${-(t ?? 18)}deg)}50%{transform:rotate(${t ?? 18}deg)}}`;
    case "rollIn":
      return `@keyframes ${kf}{from{opacity:0;transform:translateX(-100%) rotate(-120deg)}to{opacity:1;transform:translateX(0) rotate(0)}}`;
    case "rollOut":
      return `@keyframes ${kf}{from{opacity:1;transform:translateX(0) rotate(0)}to{opacity:0;transform:translateX(100%) rotate(120deg)}}`;
    case "spiralIn":
      return `@keyframes ${kf}{from{opacity:0;transform:rotate(-720deg) scale(0)}to{opacity:1;transform:rotate(0) scale(1)}}`;
    case "dropBounce":
      return `@keyframes ${kf}{0%{opacity:0;transform:translateY(-260px)}55%{opacity:1;transform:translateY(0)}70%{transform:translateY(-30px)}85%{transform:translateY(0)}93%{transform:translateY(-10px)}100%{transform:translateY(0)}}`;
    case "flipInX":
      return `@keyframes ${kf}{from{opacity:0;transform:perspective(600px) rotateX(90deg)}to{opacity:1;transform:perspective(600px) rotateX(0)}}`;
    case "flipInY":
      return `@keyframes ${kf}{from{opacity:0;transform:perspective(600px) rotateY(90deg)}to{opacity:1;transform:perspective(600px) rotateY(0)}}`;
    case "cube3d":
      return `@keyframes ${kf}{0%{transform:perspective(600px) rotateY(0) rotateX(0)}50%{transform:perspective(600px) rotateY(180deg) rotateX(12deg)}100%{transform:perspective(600px) rotateY(360deg) rotateX(0)}}`;
    case "blurIn":
      return `@keyframes ${kf}{from{opacity:0;filter:blur(14px)}to{opacity:1;filter:blur(0)}}`;
    case "blurPulse":
      return `@keyframes ${kf}{0%,100%{filter:blur(0)}50%{filter:blur(3px)}}`;
    case "brightnessPulse":
      return `@keyframes ${kf}{0%,100%{filter:brightness(1)}50%{filter:brightness(1.9)}}`;
    case "saturatePulse":
      return `@keyframes ${kf}{0%,100%{filter:saturate(1)}50%{filter:saturate(2.6)}}`;
    case "neonGlow": {
      const gc = a.glowColor || a.toColor || "#00E5FF";
      return `@keyframes ${kf}{0%,100%{filter:drop-shadow(0 0 1px ${gc})}50%{filter:drop-shadow(0 0 8px ${gc}) drop-shadow(0 0 16px ${gc})}}`;
    }
    default:
      return "";
  }
}

// ===== Configurable "Draw On" animation (绘制动效) =====
// A persistent, fully-configurable version of the one-shot generation effect:
//   - text          -> typewriter (clip rect reveals glyphs left->right, steps)
//   - open / stroke  -> stroke-draw (the outline draws from start to end)
//   - filled shapes  -> wipe reveal (clip rect sweeps left->right)
// Respects all animation params (duration / delay / easing / repeat /
// direction / fill-mode) so it can play once, loop, or reverse (undraw).
function drawPlan(el) {
  const a = el.attrs || {};
  if (el.type === "text") return "type";
  const sw = Number(a.strokeWidth) || 0;
  const hasStroke = sw > 0 && a.stroke && a.stroke !== "none";
  const hasFill = a.fill && a.fill !== "none";
  const openStroke =
    el.type === "line" ||
    el.type === "polyline" ||
    (el.type === "path" && (a.fill == null || a.fill === "none"));
  if (openStroke || (hasStroke && !hasFill)) return "stroke";
  return "wipe";
}

function buildDrawAnimation(el, a, bbox) {
  const ni = a._n || 0;
  const cls = `anim-${el.id}-${ni}`;
  const kf = `kf-${el.id}-${ni}`;
  const dur = `${a.duration || 1}s`;
  const delay = `${a.delay || 0}s`;
  const easing = a.easing || "ease";
  const repeat = a.repeat || "infinite";
  const direction = a.direction || "normal";
  const fillMode = a.fillMode || "both";
  const plan = drawPlan(el);

  if (plan === "stroke") {
    // stroke-dasharray + stroke-dashoffset reveals the path progressively.
    // For arrows, the main stroke and both decorations are independent paths
    // that share the same animation class + pathLength=1, so all three draw
    // in sync (each from 0% to 100% over the same duration).
    const css = `@keyframes ${kf}{from{stroke-dashoffset:1}to{stroke-dashoffset:0}}
.${cls}{stroke-dasharray:1;stroke-dashoffset:1;animation-name:${kf};animation-duration:${dur};animation-timing-function:${easing};animation-delay:${delay};animation-iteration-count:${repeat};animation-direction:${direction};animation-fill-mode:${fillMode};}
.paused .${cls}{animation-play-state:paused!important;}`;
    return { className: cls, css, clip: null, pathLength: true };
  }

  // type / wipe: a clip rect that scales from 0 -> full width reveals the
  // element. Text uses discrete steps (typewriter); shapes use the easing.
  const clipId = `animclip-${el.id}-${ni}`;
  const rectCls = `animrect-${el.id}-${ni}`;
  const n = Math.max(1, [...(el.attrs?.text || "")].length);
  const timing = plan === "type" ? `steps(${n})` : easing;
  const css = `@keyframes ${kf}{from{transform:scaleX(0)}to{transform:scaleX(1)}}
.${rectCls}{transform-box:fill-box;transform-origin:left center;animation-name:${kf};animation-duration:${dur};animation-timing-function:${timing};animation-delay:${delay};animation-iteration-count:${repeat};animation-direction:${direction};animation-fill-mode:${fillMode};}
.paused .${rectCls}{animation-play-state:paused!important;}
.${cls}{clip-path:url(#${clipId});}`;

  let clip = null;
  if (bbox && (bbox.w || bbox.h)) {
    const pad = el.type === "text"
      ? Math.max(2, (Number(el.attrs?.fontSize) || 32) * 0.08)
      : Math.max(1, (Number(el.attrs?.strokeWidth) || 0) / 2 + 1);
    clip = { id: clipId, cls: rectCls, x: bbox.x - pad, y: bbox.y - pad, w: bbox.w + pad * 2, h: bbox.h + pad * 2 };
  }
  return { className: cls, css, clip, pathLength: false };
}

export function buildAnimationCSS(el, bbox) {
  const a = el.animation;
  if (!a || !a.type || a.type === "none") return null;
  if (a.type === "drawOn") return buildDrawAnimation(el, a, bbox);
  // Include a per-assignment nonce in the class/keyframes names so the
  // browser re-triggers the animation whenever the user applies a new
  // animation (even if the type is the same). Without this, switching from
  // e.g. fade → fadeUp keeps the same `animation-name` (`kf-<id>`), so the
  // browser treats it as a parameter update of the same animation and never
  // replays it. The nonce is stamped on the animation object by
  // applyAnimation / onTypeChange / setAnim.
  const n = a._n || 0;
  const cls = `anim-${el.id}-${n}`;
  const kf = `kf-${el.id}-${n}`;
  const dur = `${a.duration || 1}s`;
  const delay = `${a.delay || 0}s`;
  const easing = a.easing || "ease";
  const repeat = a.repeat || "infinite";
  const direction = a.direction || "normal";
  const fillMode = a.fillMode || "both";

  const keyframes = keyframesFor(kf, a);
  if (!keyframes) return null;

  let originRule;
  if (a.originX != null && a.originY != null) {
    originRule = `transform-origin:${a.originX}px ${a.originY}px;transform-box:view-box;`;
  } else {
    originRule = `transform-origin:${ORIGIN_MAP[a.origin] || "center"};transform-box:fill-box;`;
  }

  let extra = "";
  if (a.type === "draw" || a.type === "drawReverse") extra = "stroke-dasharray:1;";
  if (a.type === "dash") extra = `stroke-dasharray:${a.dasharray || "10 8"};`;

  let css = `${keyframes}
.${cls}{animation-name:${kf};animation-duration:${dur};animation-timing-function:${easing};animation-delay:${delay};animation-iteration-count:${repeat};animation-direction:${direction};animation-fill-mode:${fillMode};${originRule}${extra}}
.paused .${cls}{animation-play-state:paused!important;}`;

  // draw / drawReverse use stroke-dashoffset with pathLength=1.
  const pathLength = a.type === "draw" || a.type === "drawReverse";

  // For arrow elements with draw/drawReverse, the END decoration must travel
  // along the main stroke from start to end (like dragging to draw). We
  // generate a separate CSS class with an offset-distance animation so the
  // decoration follows the line's growing tip.
  //
  // The START decoration stays FIXED at the start point — it is rendered with
  // ABSOLUTE coordinates (startDecoD) in ElementRenderer, just like in WHOLE
  // mode, so its angle never changes whether or not an animation is running.
  // It must NOT use offset-path/offset-rotate, because:
  //   • offset-rotate:auto would rotate it to the path tangent, but the
  //     relative geometry is built around a fixed -x axis — the combination
  //     produces a wrong angle whenever the start tangent isn't horizontal.
  //   • The start decoration's job is to mark the line's origin; it should
  //     point outward from the start, fixed, for the whole animation.
  //
  // For ALL OTHER animation types on arrows, the arrow is treated as a single
  // whole: the animation class is applied to a <g> wrapper around all three
  // parts (main stroke + start deco + end deco) in ElementRenderer, so the
  // entire arrow animates together. No per-decoration CSS is needed here.
  let arrowEndCls = "";
  let arrowStartCls = "";
  if (isArrow(el) && (a.type === "draw" || a.type === "drawReverse")) {
    const split = splitArrowPath(el);
    if (split && split.endDecoD) {
      arrowEndCls = `anim-arrow-end-${el.id}-${n}`;
      const isReverse = a.type === "drawReverse";
      const offFrom = isReverse ? 100 : 0;
      const offTo = isReverse ? 0 : 100;
      const endKf = `kf-arrow-end-${el.id}-${n}`;
      css += `
@keyframes ${endKf}{from{offset-distance:${offFrom}%}to{offset-distance:${offTo}%}}
.${arrowEndCls}{offset-path:path('${split.mainD.replace(/'/g, "\\'")}');offset-distance:${offFrom}%;offset-rotate:auto;animation-name:${endKf};animation-duration:${dur};animation-timing-function:${easing};animation-delay:${delay};animation-iteration-count:${repeat};animation-direction:${direction};animation-fill-mode:${fillMode};}
.paused .${arrowEndCls}{animation-play-state:paused!important;}`;
    }
    // No offset-path CSS for the start decoration — it renders with absolute
    // coordinates in ElementRenderer and stays fixed at the start point.
  }
  return { className: cls, css, pathLength, arrowEndCls, arrowStartCls };
}

// Build offset-path CSS for arrow decorations in the STATIC (no-animation)
// state. Both start and end decorations use offset-path + offset-rotate:auto
// so they always align with the stroke's tangent — including on curved paths
// where the tangent changes along the way. The start deco sits at 0% and the
// end deco at 100% of the main stroke. Returns { css, arrowStartCls,
// arrowEndCls } or null if the element is not an arrow with decorations.
export function buildArrowStaticCSS(el) {
  if (!isArrow(el)) return null;
  const split = splitArrowPath(el);
  if (!split) return null;
  const mainD = split.mainD.replace(/'/g, "\\'");
  let css = "";
  let arrowStartCls = "";
  let arrowEndCls = "";
  if (split.startDecoD) {
    arrowStartCls = `anim-arrow-start-${el.id}`;
    css += `\n.${arrowStartCls}{offset-path:path('${mainD}');offset-distance:0%;offset-rotate:auto;}`;
  }
  if (split.endDecoD) {
    arrowEndCls = `anim-arrow-end-${el.id}`;
    css += `\n.${arrowEndCls}{offset-path:path('${mainD}');offset-distance:100%;offset-rotate:auto;}`;
  }
  if (!css) return null;
  return { css, arrowStartCls, arrowEndCls };
}

export function animationSignature(elements) {
  return elements
    .map((e) => {
      const a = e.attrs || {};
      const base = e.id + ":" + (e.animation ? JSON.stringify(e.animation) : "0");
      if (e.animation?.type === "drawOn") {
        return `${base}:${(a.text || "").length}:${a.fill}:${a.stroke}:${a.strokeWidth}:${a.fontSize}`;
      }
      // For draw/drawReverse on arrows, the offset-path CSS depends on the
      // main stroke `d` and the decoration geometry (arrowStart/arrowEnd/
      // strokeWidth). Include these so the CSS regenerates when the path is
      // edited (e.g. straight line → curve) or decorations change.
      if (e.animation && (e.animation.type === "draw" || e.animation.type === "drawReverse")) {
        return `${base}:${a.d}:${a.arrowStart}:${a.arrowEnd}:${a.strokeWidth}`;
      }
      // For static (no-animation) arrows, the offset-path CSS also depends on
      // the main stroke `d` and decoration types, so include them too.
      if (isArrow(e)) {
        return `${base}:${a.d}:${a.arrowStart}:${a.arrowEnd}:${a.strokeWidth}`;
      }
      return base;
    })
    .join("|");
}

// ===== Generation / "play" replay effects =====
// One-shot intro effect played when the Play / Replay buttons are pressed:
//   - text  -> typewriter (reveal char-by-char via an animated clip rect)
//   - open stroke / stroke-only shapes -> stroke-draw (the outline draws on)
//   - stroke + fill shapes -> draw the outline first, then a scale pop ("先描边再缩放")
//   - fill-only shapes -> grow from center (mimics the drag-to-create gesture)
const _clamp = (min, v, max) => Math.max(min, Math.min(max, v));

export function generationPlan(el, bbox) {
  const a = el.attrs || {};
  if (el.type === "text") {
    const n = Math.max(1, [...(a.text || "")].length);
    return { mode: "type", n, duration: Number(_clamp(0.5, n * 0.09, 3).toFixed(2)) };
  }
  const sw = Number(a.strokeWidth) || 0;
  const hasStroke = sw > 0 && a.stroke && a.stroke !== "none";
  const hasFill = a.fill && a.fill !== "none";
  const openStroke =
    el.type === "line" ||
    el.type === "polyline" ||
    (el.type === "path" && (a.fill == null || a.fill === "none"));
  const size = bbox ? bbox.w + bbox.h : 400;
  if (openStroke || (hasStroke && !hasFill)) {
    return { mode: "draw", duration: Number(_clamp(0.7, size / 520, 2).toFixed(2)) };
  }
  if (hasStroke && hasFill) {
    return { mode: "strokeScale", duration: Number(_clamp(1, size / 520 + 0.6, 2.2).toFixed(2)) };
  }
  return { mode: "grow", duration: 0.6 };
}

export function generationDuration(el, bbox) {
  return generationPlan(el, bbox).duration;
}

// Build the one-shot generation CSS for an element. Returns
// { className, css, clip, pathLength } or null.
export function buildGenerationCSS(el, bbox) {
  const plan = generationPlan(el, bbox);
  const cls = `gen-${el.id}`;
  const kf = `genkf-${el.id}`;
  const dur = `${plan.duration}s`;

  if (plan.mode === "type") {
    if (!bbox || (!bbox.w && !bbox.h)) return null;
    const clipId = `genclip-${el.id}`;
    const rectCls = `genrect-${el.id}`;
    const pad = Math.max(2, (Number(el.attrs.fontSize) || 32) * 0.08);
    const clip = { id: clipId, cls: rectCls, x: bbox.x - pad, y: bbox.y - pad, w: bbox.w + pad * 2, h: bbox.h + pad * 2 };
    const css = `@keyframes ${kf}{from{transform:scaleX(0)}to{transform:scaleX(1)}}
.${rectCls}{transform-box:fill-box;transform-origin:left center;animation:${kf} ${dur} steps(${plan.n}) forwards;}
.paused .${rectCls}{animation-play-state:paused!important;}
.${cls}{clip-path:url(#${clipId});}`;
    return { className: cls, css, clip, pathLength: false };
  }

  if (plan.mode === "draw") {
    // Respect the user's animation type: drawReverse undraws (offset 0→1),
    // normal draw draws (offset 1→0).
    const isReverse = el.animation?.type === "drawReverse";
    const mainFrom = isReverse ? 0 : 1;
    const mainTo = isReverse ? 1 : 0;
    let css = `@keyframes ${kf}{from{stroke-dashoffset:${mainFrom}}to{stroke-dashoffset:${mainTo}}}
.${cls}{stroke-dasharray:1;stroke-dashoffset:${mainFrom};animation:${kf} ${dur} ease-out forwards;}
.paused .${cls}{animation-play-state:paused!important;}`;
    // For arrows, the end decoration travels along the main stroke. The start
    // decoration stays fixed at the start point — rendered with absolute
    // coordinates in ElementRenderer (no offset-path), so its angle never
    // changes during the generation animation.
    let arrowEndCls = "";
    let arrowStartCls = "";
    if (isArrow(el)) {
      const split = splitArrowPath(el);
      if (split && split.endDecoD) {
        arrowEndCls = `gen-arrow-end-${el.id}`;
        const offFrom = isReverse ? 100 : 0;
        const offTo = isReverse ? 0 : 100;
        const endKf = `genkf-arrow-end-${el.id}`;
        css += `
@keyframes ${endKf}{from{offset-distance:${offFrom}%}to{offset-distance:${offTo}%}}
.${arrowEndCls}{offset-path:path('${split.mainD.replace(/'/g, "\\'")}');offset-distance:${offFrom}%;offset-rotate:auto;animation:${endKf} ${dur} ease-out forwards;}
.paused .${arrowEndCls}{animation-play-state:paused!important;}`;
      }
      // No offset-path CSS for the start decoration — it renders with absolute
      // coordinates in ElementRenderer and stays fixed at the start point.
    }
    return { className: cls, css, clip: null, pathLength: true, arrowEndCls, arrowStartCls };
  }

  if (plan.mode === "strokeScale") {
    const css = `@keyframes ${kf}{0%{stroke-dashoffset:1;fill-opacity:0;transform:scale(1)}45%{stroke-dashoffset:0;fill-opacity:0;transform:scale(1)}60%{fill-opacity:1;transform:scale(.9)}80%{transform:scale(1.05)}100%{stroke-dashoffset:0;fill-opacity:1;transform:scale(1)}}
.${cls}{stroke-dasharray:1;transform-box:fill-box;transform-origin:center;animation:${kf} ${dur} ease-out forwards;}
.paused .${cls}{animation-play-state:paused!important;}`;
    return { className: cls, css, clip: null, pathLength: true };
  }

  // grow (fill-only)
  const css = `@keyframes ${kf}{0%{transform:scale(0);opacity:.35}70%{transform:scale(1.06);opacity:1}100%{transform:scale(1);opacity:1}}
.${cls}{transform-box:fill-box;transform-origin:center;animation:${kf} ${dur} cubic-bezier(.34,1.56,.64,1) forwards;}
.paused .${cls}{animation-play-state:paused!important;}`;
  return { className: cls, css, clip: null, pathLength: false };
}

// ---- SVG export ----
export function buildSvgMarkup(elements, { width = 800, height = 600, x = 0, y = 0, playing = true, background = "transparent", includeAnimations = true } = {}) {
  const styles = [];
  const defs = [];
  const body = elements.map((el) => renderElementString(el, styles, defs, includeAnimations)).join("\n  ");
  const defBlock =
    styles.length || defs.length
      ? `<defs>${defs.join("")}${styles.length ? `<style>${styles.join("\n")}</style>` : ""}</defs>\n  `
      : "";
  const cls = playing ? "" : ' class="paused"';
  const bg = background && background !== "transparent"
    ? `<rect x="${x}" y="${y}" width="${width}" height="${height}" fill="${background}" />\n  `
    : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${x} ${y} ${width} ${height}" width="${width}" height="${height}"${cls}>
  ${defBlock}${bg}${body}
</svg>`;
}

function escapeXml(s) {
  return String(s).replace(/[<>&"']/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" }[c]));
}

function attrsToString(attrs, skip = ["text"]) {
  return Object.entries(attrs)
    .filter(([k]) => !skip.includes(k))
    .map(([k, v]) => `${attrToSvgName(k)}="${escapeXml(v)}"`)
    .join(" ");
}

function renderElementString(el, styles, defs, includeAnimations = true) {
  let classAttr = "";
  let animType = null;
  let animPathLength = false;
  if (includeAnimations) {
    const anim = buildAnimationCSS(el, bboxOf(el));
    if (anim) {
      styles.push(anim.css);
      if (anim.clip) {
        defs.push(
          `<clipPath id="${anim.clip.id}" clipPathUnits="userSpaceOnUse"><rect class="${anim.clip.cls}" x="${anim.clip.x}" y="${anim.clip.y}" width="${anim.clip.w}" height="${anim.clip.h}" /></clipPath>`
        );
      }
      classAttr = ` class="${anim.className}"`;
      animPathLength = !!anim.pathLength;
    }
    // Static arrows render as a whole <g> with absolute-coord decorations —
    // no offset-path CSS is needed.
    animType = el.animation?.type;
  }
  const ed = effectDefsString(el);
  if (ed) defs.push(ed);
  const a = { ...(el.attrs || {}) };
  const rot = Number(a.rotation) || 0;
  delete a.rotation;
  const fr = fillRef(el);
  if (fr) a.fill = fr;

  // Image-specific: corner radius + crop (clipPath) + horizontal/vertical flip
  // merged into the transform alongside rotation. Strip the non-SVG control
  // attrs so they don't leak into the serialized <image> tag.
  //
  // Rect-specific: when a corner radius is set, clip via a rounded-rect path
  // (per-corner radii) — identical to image. Native <rect rx> only supports a
  // single uniform radius, so we use clipPath for parity with image.
  let clipAttr = "";
  if (el.type === "image") {
    const unit = a.cornerRadiusUnit === "%" ? "%" : "px";
    // Collect every corner-related + crop control attr to strip from the tag.
    const controlKeys = [
      "cornerRadius",
      "cornerRadiusUnit",
      "cornerIndividual",
      "cornerTL",
      "cornerTR",
      "cornerBR",
      "cornerBL",
      "cropX",
      "cropY",
      "cropW",
      "cropH",
      "origX",
      "origY",
      "origW",
      "origH",
    ];
    controlKeys.forEach((k) => delete a[k]);
    const flipH = Number(a.flipH) === -1 ? -1 : 1;
    const flipV = Number(a.flipV) === -1 ? -1 : 1;
    delete a.flipH;
    delete a.flipV;
    // Place the <image> at the original (uncropped) source box so the full
    // source is available; clipPath restricts the visible region to the
    // cropped display box (x/y/width/height).
    const ox = el.attrs.origX ?? el.attrs.x ?? 0;
    const oy = el.attrs.origY ?? el.attrs.y ?? 0;
    const ow = el.attrs.origW ?? el.attrs.width ?? 0;
    const oh = el.attrs.origH ?? el.attrs.height ?? 0;
    a.x = ox;
    a.y = oy;
    a.width = ow;
    a.height = oh;
    const cx = el.attrs.x ?? 0;
    const cy = el.attrs.y ?? 0;
    const cw = el.attrs.width ?? 0;
    const ch = el.attrs.height ?? 0;
    const hasCrop = ox !== cx || oy !== cy || ow !== cw || oh !== ch;
    if (hasCornerRadius(el.attrs) || hasCrop) {
      const { rTL, rTR, rBR, rBL } = resolveCornerRadii(el.attrs, cw, ch);
      const d = roundedRectPath(cx, cy, cw, ch, rTL, rTR, rBR, rBL);
      const clipId = `clip-${el.id}`;
      defs.push(
        `<clipPath id="${clipId}" clipPathUnits="userSpaceOnUse"><path d="${d}"/></clipPath>`
      );
      clipAttr = ` clip-path="url(#${clipId})"`;
    }
    if (flipH === -1 || flipV === -1 || rot) {
      const c = centerOf(bboxOf(el));
      const parts = [];
      if (flipH === -1 || flipV === -1) {
        parts.push(`translate(${c.x} ${c.y}) scale(${flipH} ${flipV}) translate(${-c.x} ${-c.y})`);
      }
      if (rot) parts.push(`rotate(${rot} ${c.x} ${c.y})`);
      a.transform = parts.join(" ");
    }
  } else if (el.type === "rect") {
    // Always strip corner control attrs + native rx/ry so they don't leak
    // into the serialized <rect> tag. When a corner radius is set, a clipPath
    // handles the rounding; when it isn't, the rect stays a plain rectangle.
    const controlKeys = [
      "cornerRadius",
      "cornerRadiusUnit",
      "cornerIndividual",
      "cornerTL",
      "cornerTR",
      "cornerBR",
      "cornerBL",
      "rx",
      "ry",
    ];
    controlKeys.forEach((k) => delete a[k]);
    if (hasCornerRadius(el.attrs)) {
      const cx = el.attrs.x ?? 0;
      const cy = el.attrs.y ?? 0;
      const cw = el.attrs.width ?? 0;
      const ch = el.attrs.height ?? 0;
      const { rTL, rTR, rBR, rBL } = resolveCornerRadii(el.attrs, cw, ch);
      const d = roundedRectPath(cx, cy, cw, ch, rTL, rTR, rBR, rBL);
      const clipId = `clip-${el.id}`;
      defs.push(
        `<clipPath id="${clipId}" clipPathUnits="userSpaceOnUse"><path d="${d}"/></clipPath>`
      );
      clipAttr = ` clip-path="url(#${clipId})"`;
    }
    if (rot) {
      const c = centerOf(bboxOf(el));
      a.transform = `rotate(${rot} ${c.x} ${c.y})`;
    }
  } else if (rot) {
    const c = centerOf(bboxOf(el));
    a.transform = `rotate(${rot} ${c.x} ${c.y})`;
  }
  if (animType === "draw" || animType === "drawReverse" || animPathLength) {
    a.pathLength = 1;
  }
  const flt = filterRef(el);
  const extra = flt ? ` filter="${flt}"` : "";
  if (el.type === "text") {
    const { text = "", ...rest } = a;
    return `<text ${attrsToString(rest)}${classAttr}${extra}>${escapeXml(text)}</text>`;
  }
  // Arrow elements: the endpoint decorations are embedded in the path `d` as
  // line/fill segments, so no <marker> def or marker-end attribute is needed.
  // The `arrow`, `arrowStart`, `arrowEnd` and `lineEdit` flags are meta-attrs
  // that should not appear in the exported SVG.
  if (a.arrow === true || a.arrow === "true") delete a.arrow;
  if (a.arrowStart) delete a.arrowStart;
  if (a.arrowEnd) delete a.arrowEnd;
  if (a.lineEdit) delete a.lineEdit;

  // Arrow elements are a SINGLE graphic. An animation applies to the WHOLE
  // arrow (main stroke + both decorations together), not to the parts
  // individually. Two export modes mirror ElementRenderer:
  //   • WHOLE mode (static + non-draw animations): wrap all parts in a <g>
  //     carrying the animation class. Decorations use ABSOLUTE coords.
  //   • SPLIT mode (draw / drawReverse only): parts are split so the end
  //     decoration can travel along the growing stroke (offset-path).
  if (isArrow(el)) {
    const split = splitArrowPath(el);
    if (split) {
      const isSplit = animType === "draw" || animType === "drawReverse";

      if (isSplit) {
        // SPLIT mode: main stroke + decorations with offset-path classes.
        const usePathLength = true;
        const mainAttrs = { ...a, d: split.mainD };
        if (usePathLength) mainAttrs.pathLength = 1;
        const mainStr = `<path ${attrsToString(mainAttrs)}${classAttr}${extra}${clipAttr} />`;
        const parts = [mainStr];

        let startCls = "";
        let endCls = "";
        if (includeAnimations) {
          const anim = buildAnimationCSS(el, bboxOf(el));
          if (anim) {
            startCls = anim.arrowStartCls || "";
            endCls = anim.arrowEndCls || "";
          }
        }

        if (split.startDecoD) {
          // Start decoration: ABSOLUTE coords, fixed at the start point. It
          // does NOT use offset-path (unlike the end decoration), so its
          // angle stays constant throughout the draw animation. No animation
          // class, no pathLength — it is a static marker at the line's origin.
          const decoAttrs = { ...a, d: split.startDecoD };
          if (isFilledDeco(split.sType)) decoAttrs.fill = a.stroke || "#000";
          delete decoAttrs.pathLength;
          parts.push(`<path ${attrsToString(decoAttrs)}${extra}${clipAttr} />`);
        }
        if (split.endDecoD) {
          const decoAttrs = { ...a, d: split.endDecoDRelative };
          if (isFilledDeco(split.eType)) decoAttrs.fill = a.stroke || "#000";
          if (usePathLength) decoAttrs.pathLength = 1;
          const decoClass = endCls ? ` class="${endCls}"` : classAttr;
          parts.push(`<path ${attrsToString(decoAttrs)}${decoClass}${extra}${clipAttr} />`);
        }
        return parts.join("\n  ");
      }

      // WHOLE mode: <g class="anim-..."> wraps all parts (absolute coords).
      // Inner paths carry no animation class — the <g> animates the whole.
      const innerAttrs = { ...a };
      delete innerAttrs.className;
      const mainStr = `<path ${attrsToString({ ...innerAttrs, d: split.mainD })}${extra}${clipAttr} />`;
      const parts = [mainStr];
      if (split.startDecoD) {
        const decoAttrs = { ...innerAttrs, d: split.startDecoD };
        if (isFilledDeco(split.sType)) decoAttrs.fill = a.stroke || "#000";
        parts.push(`<path ${attrsToString(decoAttrs)}${extra}${clipAttr} />`);
      }
      if (split.endDecoD) {
        const decoAttrs = { ...innerAttrs, d: split.endDecoD };
        if (isFilledDeco(split.eType)) decoAttrs.fill = a.stroke || "#000";
        parts.push(`<path ${attrsToString(decoAttrs)}${extra}${clipAttr} />`);
      }
      return `<g${classAttr}>\n  ${parts.join("\n  ")}\n</g>`;
    }
  }

  // Polygon with corner radius: export as a <path> with rounded corners.
  if (el.type === "polygon" && hasCornerRadius(a) && a.points) {
    const pts = parsePoints(a.points);
    const bb = bboxOf(el);
    const r = resolveCornerRadius(a, bb.w, bb.h);
    if (r > 0 && pts.length >= 3) {
      const d = roundedPolygonPath(pts, r);
      const { points, ...rest } = a;
      return `<path ${attrsToString({ ...rest, d })}${classAttr}${extra}${clipAttr} />`;
    }
  }

  return `<${el.type} ${attrsToString(a)}${classAttr}${extra}${clipAttr} />`;
}
